import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { warehouseAPI } from '../api/warehouseAPI'
import { locationAPI } from '../api/locationAPI'
import { inventoryItemTransferAPI } from '../api/inventoryItemTransferAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { formatQuantity } from '../utils/commonUtils'
import TransferItemCard from '../components/TransferItemCard'
import 'material-icons/iconfont/material-icons.css';
import Select from 'react-select'

function TransferReceiveScreen() {
    const navigate = useNavigate()
    const { transferId } = useParams()
    const [error, setError] = useState(null)
    const [warehouses, setWarehouses] = useState([])
    const [locations, setLocations] = useState([])
    const [transfer, setTransfer] = useState({
        stock_item: null,
        inventory_item: null,
        source_warehouse: { _id: '' },
        destination_warehouse: { _id: '' },
        source_location: '',
        destination_location: '',
        quantity: '',
        comments: '',
        receivedQuantity: '',
        hasQuantityDiscrepancy: false
    })

    useEffect(() => {
        async function fetchTransfer() {
            try {
                console.log('Fetching transfer:', transferId)
                const item = await inventoryItemTransferAPI.getById(transferId)
                
                if (!item) return;

                const formattedTransfer = {
                    stock_item: item.stock_item,
                    inventory_item: item.inventory_item,
                    source_warehouse: item.source_warehouse,
                    destination_warehouse: item.destination_warehouse,
                    source_location: item.source_location?.name || '',
                    destination_location: item.destination_location?.name || '',
                    quantity: item.quantity_sent || '',
                    comments: '',
                    receivedQuantity: '',
                    hasQuantityDiscrepancy: false
                }
                
                if (item.destination_warehouse) {
                    const warehouseLocations = await locationAPI.getAll(item.destination_warehouse._id)
                    setLocations(warehouseLocations)
                    
                    // Find the first location ending with "-FLOOR"
                    const floorLocation = warehouseLocations.find(loc => loc.name.endsWith('-FLOOR'))
                    if (floorLocation) {
                        formattedTransfer.destination_location = floorLocation
                    }
                }
                
                setTransfer(formattedTransfer)
            } catch (error) {
                console.error('Error fetching transfer:', error)
                setError('Failed to load transfer')
            }
        }

        if (transferId && warehouses.length > 0) {
            fetchTransfer()
        }
    }, [transferId, warehouses])

    useEffect(() => {
        async function fetchWarehouses() {
            try {
                const response = await warehouseAPI.getAll()
                setWarehouses(response)
            } catch (error) {
                setError('Failed to load warehouses')
            }
        }
        fetchWarehouses()
    }, [])

    const submitHandler = async (e, preferredResolution = 'auto') => {
        e.preventDefault()
        
        // Validate quantity before sending
        const quantityToSend = transfer.hasQuantityDiscrepancy ? 
            parseInt(transfer.receivedQuantity) : 
            parseInt(transfer.quantity)

        if (!quantityToSend || isNaN(quantityToSend)) {
            setError('Please enter a valid quantity')
            return
        }

        try {
            await inventoryItemTransferAPI.receiveTransfer(
                transferId, 
                transfer.destination_location._id, 
                transfer.comments,
                quantityToSend, // Send the parsed integer
                transfer.hasQuantityDiscrepancy,
                preferredResolution
            )
            navigate('/inventory-transfers')
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const locationOptions = locations.map(location => ({
        value: location._id,
        label: location.name
    }))

    return (
        <Container>
            <Row className="justify-content-left">
            <Col style={{ maxWidth: '730px' }}>
            <h1>Receive Transfer {transferId}</h1>
            {transfer?.inventory_item && (
                <TransferItemCard inventoryItem={transfer.inventory_item} />
            )}
            <div style={{ height: '20px' }}></div>
            <Form onSubmit={submitHandler}>
                <h4 className="mt-4">Confirm Receive of {formatQuantity(transfer?.quantity)} items</h4>
                <Form.Group className="mb-3" controlId="destinationWarehouse">
                    <Form.Label>Destination Warehouse</Form.Label>
                    <Form.Control
                        type="text" 
                        value={transfer?.destination_warehouse?.name || ''}
                        disabled
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="destinationLocation">
                    <Form.Label>Destination Location</Form.Label>
                    <Select
                        value={locationOptions.find(option => option.value === transfer.destination_location?._id)}
                        onChange={(selectedOption) => setTransfer({
                            ...transfer,
                            destination_location: locations.find(l => l._id === selectedOption.value)
                        })}
                        options={locationOptions}
                        isSearchable
                        placeholder="Select destination location..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </Form.Group>

                <Form.Group className="mb-3" controlId="receivedQuantity">
                    <Form.Check
                        type="checkbox"
                        label="Received quantity doesn't match expected amount"
                        checked={transfer.hasQuantityDiscrepancy}
                        onChange={(e) => setTransfer({
                            ...transfer,
                            hasQuantityDiscrepancy: e.target.checked,
                            receivedQuantity: e.target.checked ? '' : transfer.quantity
                        })}
                    />
                    <Form.Control
                        type="number"
                        step="1"
                        min="1"
                        value={transfer.hasQuantityDiscrepancy ? transfer.receivedQuantity : transfer.quantity}
                        onChange={(e) => setTransfer({
                            ...transfer,
                            receivedQuantity: e.target.value
                        })}
                        disabled={!transfer.hasQuantityDiscrepancy}
                        required
                        placeholder="Enter received quantity"
                        autoComplete="off"
                        spellCheck="false"
                    />
                    <Form.Text className="text-muted">
                        Expected quantity: {formatQuantity(transfer.quantity)}
                    </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-4" controlId="comments">
                    <Form.Label>Notes {transfer.hasQuantityDiscrepancy && <span className="text-danger">*</span>}</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        maxLength={500}
                        value={transfer.comments}
                        onChange={(e) => setTransfer({...transfer, comments: e.target.value})}
                        placeholder="Enter notes (max 500 characters)"
                        required={transfer.hasQuantityDiscrepancy}
                        autoComplete="off"
                        spellCheck="true"
                    />
                    <Form.Text className="text-muted">
                        {transfer.comments?.length || 0}/500 characters
                        {transfer.hasQuantityDiscrepancy && <span className="text-danger ms-2">Required when quantity doesn&apos;t match</span>}
                    </Form.Text>
                </Form.Group>
                {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
                )}
                {transfer.hasQuantityDiscrepancy ? (
                    <div className="d-flex gap-2">
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip>
                                    The inventory will be received at the destination with the actual received quantity, and an issue will be raised for investigation.
                                </Tooltip>
                            }
                        >
                            <Button 
                                variant="warning" 
                                type="submit" 
                                onClick={(e) => submitHandler(e, 'manual')}
                            >
                                Receive and Report Issue
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                <Tooltip>
                                    The inventory will be received at the destination with the actual received quantity, and the system will attempt to automatically adjust the excess/shortage at the sender&apos;s location.
                                </Tooltip>
                            }
                        >
                            <Button 
                                variant="info" 
                                type="button"
                                onClick={(e) => submitHandler(e, 'auto')}
                            >
                                Receive and Resolve
                            </Button>
                        </OverlayTrigger>
                        <Button 
                            variant="secondary" 
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </Button>
                    </div>
                ) : (
                    <div className="d-flex gap-2">
                        <Button variant="success" type="submit" onClick={(e) => submitHandler(e, '')}>
                            Receive
                        </Button>
                        <Button 
                            variant="secondary" 
                            onClick={() => navigate(-1)}
                        >
                            Cancel
                        </Button>
                    </div>
                )}
            </Form>
            
            </Col>
            </Row>
        </Container>
    )
}

export default TransferReceiveScreen 